import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import Logo from '../components/common/Logo';

// 3D Secure callback page - handles return from Ziraat Bank 3D authentication
// SIMPLIFIED: Bank redirect = success. No polling needed, email confirms details.

const PaymentCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Starts undecided. Defaulting to 'success' meant every visit — including one
  // that turned out to be a failure — flashed a "Payment Successful" screen first.
  const [status, setStatus] = useState<'checking' | 'success' | 'failed'>('checking');
  const [linkId, setLinkId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    // Check URL path for status (/pay/callback/success or /pay/callback/failed)
    const pathStatus = window.location.pathname.includes('/success') 
      ? 'success' 
      : window.location.pathname.includes('/failed') 
      ? 'failed' 
      : null;

    const callbackStatus = pathStatus || searchParams.get('status');
    const linkIdParam = searchParams.get('linkId');
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    
    // Save linkId to state
    if (linkIdParam) {
      setLinkId(linkIdParam);
    }

    console.log('3D Secure callback received:', {
      status: callbackStatus,
      linkId: linkIdParam,
      error,
      message,
      pathStatus,
    });

    // Handle error/failed case
    if (error || callbackStatus === 'failed') {
      setStatus('failed');
      setErrorMessage(
        message
          ? decodeURIComponent(message)
          : error === 'missing_order_id'
          ? 'Order ID is missing. Please try again.'
          : error === 'order_not_found'
          ? 'Payment link not found. Please check your link and try again.'
          : error === '3d_failed'
          ? '3D Secure authentication failed. Please try again.'
          : error === 'system_error'
          ? 'System error occurred. Please contact support.'
          : 'Payment failed. Please try again.'
      );
      return;
    }

    // Handle success case - Bank redirected to success URL = payment successful
    if (callbackStatus === 'success') {
      setStatus('success');
      return;
    }

    // Default based on URL path
    if (window.location.pathname.includes('/success')) {
      setStatus('success');
    } else {
      setStatus('failed');
      setErrorMessage('Invalid payment callback.');
    }
  }, [searchParams]);

  const handleBackToPayment = () => {
    const targetLinkId = linkId || searchParams.get('linkId');
    if (targetLinkId) {
      navigate(`/pay/${targetLinkId}`);
    } else {
      navigate('/');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        elevation={2}
        sx={{
          backgroundColor: '#f5f5f5',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'center' }}>
          <Logo size="medium" />
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card
          sx={{
            boxShadow: 3,
            textAlign: 'center',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            {/* Undecided State - shown until the result in the URL is read */}
            {status === 'checking' && (
              <>
                <CircularProgress size={72} sx={{ mb: 3 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                  Checking your payment…
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  Please wait while we confirm the result with the bank.
                </Typography>
              </>
            )}

            {/* Success State */}
            {status === 'success' && (
              <>
                <CheckCircle sx={{ fontSize: 100, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'success.main' }}>
                  Payment Successful!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
                  Thank you! Your payment has been received and confirmed by the bank.
                </Typography>

                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    backgroundColor: '#e8f5e9',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    📧 You will receive a confirmation email shortly with your payment details.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleBackToPayment}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    px: 4,
                    fontWeight: 700,
                  }}
                >
                  Back to Payment Page
                </Button>
              </>
            )}

            {/* Failed State */}
            {status === 'failed' && (
              <>
                <ErrorIcon sx={{ fontSize: 100, color: 'error.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'error.main' }}>
                  Payment Failed
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  {errorMessage || 'Your payment could not be processed.'}
                </Typography>

                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: '#ffebee',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Please check your card details and try again, or contact your bank for assistance.
                  </Typography>
                </Box>

                <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleBackToPayment}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontWeight: 700,
                    }}
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleGoHome}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontWeight: 700,
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Alexa Travel Agency. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentCallbackPage;
